import type * as React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFragment, useRelayEnvironment } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import submitContactForm from '../../mutations/submitContactFormMutation';
import { contactFormFragment } from './ContactForm.relay';
import { contactFormClassName } from './lib/ContactFormStyles';
import { FormItem } from './lib/formItem';
import type { ContactFormPropsType, FormItemGraphQLType, FormItemGroupGraphQLType, FormItemInputType, MultiLineTextGraphQLType, RadioGroupGraphQLType, SingleLineTextGraphQLType } from './types';

const flattenFormItems = (formItems: readonly FormItemGraphQLType[]): FormItemInputType[] => {
  return formItems.reduce((acc: FormItemInputType[], formItem: FormItemGraphQLType) => {
    switch (formItem.__typename) {
      case 'FormItemGroup':
        const groupItem = formItem as FormItemGroupGraphQLType;
        return acc.concat(flattenFormItems(groupItem.items));
      case 'RadioGroup':
        const radioItem = formItem as RadioGroupGraphQLType;
        return acc.concat(
          {
            name: radioItem.name,
            value: '',
          },
          flattenFormItems(radioItem.options.filter((option) => option.attached !== null).map((option) => option.attached)),
        );
      case 'SingleLineText':
      case 'MultiLineText':
        const textItem = formItem as SingleLineTextGraphQLType | MultiLineTextGraphQLType;
        return acc.concat({
          name: textItem.name,
          value: '',
        });
      default:
        // tslint:disable-next-line
        console.warn('unknown FormItem', formItem.__typename);
        return acc;
    }
  }, []);
};

const ContactForm: React.FC<ContactFormPropsType> = ({ contactForm: contactFormKey, formPageId }) => {
  const environment = useRelayEnvironment();
  const navigate = useNavigate();

  const contactForm = useFragment(contactFormFragment, contactFormKey);

  const initialInputValues = useMemo(() => flattenFormItems(contactForm.formItems as any), [contactForm.formItems]);

  const [inputValues, setInputValues] = useState<FormItemInputType[]>(initialInputValues);
  const [returnMessage, setReturnMessage] = useState<string | null>(null);

  const inputValuesRef = useRef(inputValues);
  inputValuesRef.current = inputValues;

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      submitContactForm(
        environment,
        {
          data: inputValuesRef.current,
          formPageId,
        },
        (response) => {
          const { returnMessage: message, successUrl } = response.submitContactForm;

          if (successUrl !== null) {
            navigate(successUrl);
          } else {
            setReturnMessage(message);
          }
        },
        (error) => {
          console.error('Mutation error:', error);
        },
      );
    },
    [environment, formPageId, navigate],
  );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const targetName = e.target.name;
    const targetValue = e.target.value;

    setInputValues((prev) => {
      const index = prev.findIndex((inputValue) => inputValue.name === targetName);
      if (index === -1) {
        return prev;
      }

      const newInputValues = [...prev];
      newInputValues[index] = {
        ...newInputValues[index],
        value: targetValue,
      };
      return newInputValues;
    });
  }, []);

  const formItems = contactForm.formItems as any;

  return (
    <div className={contactFormClassName}>
      {returnMessage === null ? (
        <div>
          <h3>{contactForm.header}</h3>
          <p>{contactForm.text}</p>
          <form onSubmit={handleSubmit}>
            {formItems.map((formItem, index) => {
              return <FormItem key={index} handleChange={handleChange} formItem={formItem} inputValues={inputValues} />;
            })}
            <div className="field flex-reverse">
              <input
                className="inline-block cursor-pointer rounded-[30px] border border-black bg-white px-10 py-2.5 text-[15px] font-normal font-[BonnCaps,Arial,sans-serif] uppercase tracking-wide text-[#668272] outline-none my-2.5 [.no-bonncapps_&]:font-[Arial,sans-serif]"
                type="submit"
                value={contactForm.submitButtonLabel}
                onSubmit={handleSubmit}
              />
            </div>
          </form>
        </div>
      ) : (
        <p>{returnMessage}</p>
      )}
    </div>
  );
};

export { ContactForm };
